import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Save, MapPin, Plane, Home, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import QuoteCurrencyMode from '@/components/Trip/QuoteCurrencyMode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateQuote = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canFx = ['admin', 'accounts'].includes((user?.role || '').toLowerCase());
  const [loading, setLoading] = useState(false);
  const [themes, setThemes] = useState([]);
  const [departureCities, setDepartureCities] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [showCustomDeparture, setShowCustomDeparture] = useState(false);
  const [showCustomDestination, setShowCustomDestination] = useState(false);
  const [customDeparture, setCustomDeparture] = useState('');
  const [customDestination, setCustomDestination] = useState('');
  
  const [formData, setFormData] = useState({
    guestName: '',
    email: '',
    phone: '',
    departureCity: '',
    destination: '',
    adults: 2,
    children: 0,
    infants: 0,
    baseCost: '',
    gstRate: '5',
    tcsRate: '2',
    status: 'draft',
    theme: '',
    customTheme: '',
    locationType: 'domestic',
    // Phase 12 — quote/product type selector.
    quoteType: 'custom',
    // BATCH 2C — Quote Currency Mode (default INR).
    quoteCurrencyMode: 'INR',
    quoteCurrency: 'USD',
    fxClientAmount: '',
    fxIndicativeRate: '',
    fxRateDate: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [themesRes, citiesRes, destRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/themes`),
        axios.get(`${BACKEND_URL}/api/departure-cities`),
        axios.get(`${BACKEND_URL}/api/destinations`)
      ]);
      setThemes(themesRes.data.themes || []);
      setDepartureCities(citiesRes.data.cities || []);
      setDestinations(destRes.data.destinations || []);
    } catch (error) {
      setThemes(['Honeymoon', 'Family', 'Cruise', 'Anniversary', 'Birthday', 'Friends', 'Other']);
      setDepartureCities(['Delhi', 'Mumbai', 'Kolkata', 'Chennai', 'Bangalore', 'Hyderabad']);
      setDestinations(['Bali', 'Maldives', 'Dubai', 'Singapore', 'Thailand', 'Europe']);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCustomDeparture = async () => {
    if (!customDeparture.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/departure-cities?city=${encodeURIComponent(customDeparture.trim())}`);
      setDepartureCities(prev => [...prev, customDeparture.trim()]);
      setFormData(prev => ({ ...prev, departureCity: customDeparture.trim() }));
      setCustomDeparture('');
      setShowCustomDeparture(false);
      toast.success('Departure city added!');
    } catch (error) {
      toast.error('Failed to add city');
    }
  };

  const handleAddCustomDestination = async () => {
    if (!customDestination.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/destinations?destination=${encodeURIComponent(customDestination.trim())}`);
      setDestinations(prev => [...prev, customDestination.trim()]);
      setFormData(prev => ({ ...prev, destination: customDestination.trim() }));
      setCustomDestination('');
      setShowCustomDestination(false);
      toast.success('Destination added!');
    } catch (error) {
      toast.error('Failed to add destination');
    }
  };

  const calculateTotals = () => {
    const base = parseFloat(formData.baseCost) || 0;
    const gst = base * (parseFloat(formData.gstRate) / 100);
    const subtotal = base + gst;
    const tcs = subtotal * (parseFloat(formData.tcsRate) / 100);
    const total = subtotal + tcs;
    
    return {
      baseCost: base,
      gstAmount: gst,
      subtotal,
      tcsAmount: tcs,
      totalCost: total
    };
  };

  const totals = calculateTotals();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.phone.trim() && !formData.email.trim()) {
      toast.error('Provide at least a phone number or an email address');
      return;
    }
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/quotes`, formData);
      toast.success('Trip created successfully!');
      navigate(`/quotes/${response.data.id}/edit`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = Array.isArray(detail) ? (detail[0]?.msg || 'Failed to create trip') : (detail || 'Failed to create trip');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/quotes')}
          data-testid="back-btn"
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 tracking-tight">
          Create New Trip
        </h1>
        <p className="text-base text-slate-600 mt-2">Fill in the details to create a new trip</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Guest Information */}
        <Card className="p-6 border border-slate-200 rounded-xl bg-white">
          <h2 className="text-xl font-heading font-semibold text-slate-900 mb-6">Guest Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="guestName" className="text-sm font-semibold text-slate-700">Guest Name *</Label>
              <Input
                id="guestName"
                name="guestName"
                data-testid="guest-name-input"
                value={formData.guestName}
                onChange={handleChange}
                required
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="email-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="Optional if phone is provided"
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            
            <div>
              <Label htmlFor="phone" className="text-sm font-semibold text-slate-700">Phone</Label>
              <Input
                id="phone"
                name="phone"
                data-testid="phone-input"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Optional if email is provided"
                className="mt-2 h-11 rounded-xl"
              />
              <p className="text-xs text-slate-400 mt-1">Provide at least a phone number or an email address.</p>
            </div>
            
            {/* Departure City Dropdown */}
            <div>
              <Label htmlFor="departureCity" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Plane className="h-4 w-4 rotate-45" /> Departure City *
              </Label>
              {!showCustomDeparture ? (
                <div className="mt-2 flex gap-2">
                  <select
                    id="departureCity"
                    name="departureCity"
                    value={formData.departureCity}
                    onChange={handleChange}
                    required
                    className="flex-1 h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    data-testid="departure-city-select"
                  >
                    <option value="">Select City</option>
                    {departureCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCustomDeparture(true)}
                    className="h-11 px-3 rounded-xl"
                    title="Add custom city"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <Input
                    value={customDeparture}
                    onChange={(e) => setCustomDeparture(e.target.value)}
                    placeholder="Enter city name"
                    className="flex-1 h-11 rounded-xl"
                  />
                  <Button
                    type="button"
                    onClick={handleAddCustomDeparture}
                    className="h-11 px-4 rounded-xl bg-primary-600"
                  >
                    Add
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCustomDeparture(false)}
                    className="h-11 px-3 rounded-xl"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            
            {/* Destination Dropdown */}
            <div className="md:col-span-2">
              <Label htmlFor="destination" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Destination *
              </Label>
              {!showCustomDestination ? (
                <div className="mt-2 flex gap-2">
                  <select
                    id="destination"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    required
                    className="flex-1 h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    data-testid="destination-select"
                  >
                    <option value="">Select Destination</option>
                    {destinations.map(dest => (
                      <option key={dest} value={dest}>{dest}</option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCustomDestination(true)}
                    className="h-11 px-3 rounded-xl"
                    title="Add custom destination"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <Input
                    value={customDestination}
                    onChange={(e) => setCustomDestination(e.target.value)}
                    placeholder="Enter destination name"
                    className="flex-1 h-11 rounded-xl"
                  />
                  <Button
                    type="button"
                    onClick={handleAddCustomDestination}
                    className="h-11 px-4 rounded-xl bg-primary-600"
                  >
                    Add
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCustomDestination(false)}
                    className="h-11 px-3 rounded-xl"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Trip Details */}
        <Card className="p-6 border border-slate-200 rounded-xl bg-white">
          <h2 className="text-xl font-heading font-semibold text-slate-900 mb-6">Trip Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Theme */}
            <div>
              <Label htmlFor="theme" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Trip Theme *
              </Label>
              <select
                id="theme"
                name="theme"
                value={formData.theme}
                onChange={handleChange}
                required
                className="mt-2 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                data-testid="theme-select"
              >
                <option value="">Select Theme</option>
                {themes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Custom Theme (shown only if "Other" is selected) */}
            {formData.theme === 'Other' && (
              <div>
                <Label htmlFor="customTheme" className="text-sm font-semibold text-slate-700">Custom Theme Name</Label>
                <Input
                  id="customTheme"
                  name="customTheme"
                  value={formData.customTheme}
                  onChange={handleChange}
                  placeholder="Enter custom theme"
                  className="mt-2 h-11 rounded-xl"
                />
              </div>
            )}

            {/* Location Type */}
            <div>
              <Label htmlFor="locationType" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                {formData.locationType === 'international' ? <Plane className="h-4 w-4" /> : <Home className="h-4 w-4" />}
                Location Type *
              </Label>
              <select
                id="locationType"
                name="locationType"
                value={formData.locationType}
                onChange={handleChange}
                required
                className="mt-2 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                data-testid="location-type-select"
              >
                <option value="domestic">Domestic</option>
                <option value="international">International</option>
              </select>
            </div>

            {/* Phase 12 — Quote Type selector */}
            <div>
              <Label htmlFor="quoteType" className="text-sm font-semibold text-slate-700">
                Quote Type *
              </Label>
              <select
                id="quoteType"
                name="quoteType"
                value={formData.quoteType}
                onChange={handleChange}
                required
                className="mt-2 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                data-testid="quote-type-select"
              >
                <option value="custom">Custom Package (International applies 2% TCS)</option>
                <option value="readymade">Readymade Package (International applies 2% TCS)</option>
                <option value="flight">Flight Only (no TCS)</option>
                <option value="hotel">Hotel Only (no TCS)</option>
                <option value="attraction">Attraction / Activity (no TCS)</option>
                <option value="visa">Visa Only (no TCS)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div>
              <Label htmlFor="adults" className="text-sm font-semibold text-slate-700">Adults</Label>
              <Input
                id="adults"
                name="adults"
                type="number"
                min="1"
                data-testid="adults-input"
                value={formData.adults}
                onChange={handleChange}
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            
            <div>
              <Label htmlFor="children" className="text-sm font-semibold text-slate-700">Children</Label>
              <Input
                id="children"
                name="children"
                type="number"
                min="0"
                data-testid="children-input"
                value={formData.children}
                onChange={handleChange}
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            
            <div>
              <Label htmlFor="infants" className="text-sm font-semibold text-slate-700">Infants</Label>
              <Input
                id="infants"
                name="infants"
                type="number"
                min="0"
                data-testid="infants-input"
                value={formData.infants}
                onChange={handleChange}
                className="mt-2 h-11 rounded-xl"
              />
            </div>
          </div>
        </Card>

        {/* Pricing & Tax */}
        <Card className="p-6 border border-slate-200 rounded-xl bg-white">
          <h2 className="text-xl font-heading font-semibold text-slate-900 mb-6">Pricing & Tax</h2>

          {/* BATCH 2C — Quote Currency Mode (Admin/Accounts only). */}
          <div className="mb-6">
            <QuoteCurrencyMode
              value={formData}
              onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
              canFx={canFx}
              travelStartDate={formData.travelStartDate}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="baseCost" className="text-sm font-semibold text-slate-700">Base Cost (₹) *</Label>
              <Input
                id="baseCost"
                name="baseCost"
                type="number"
                data-testid="base-cost-input"
                value={formData.baseCost}
                onChange={handleChange}
                required
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            
            <div>
              <Label htmlFor="gstRate" className="text-sm font-semibold text-slate-700">GST Rate (%)</Label>
              <select
                id="gstRate"
                name="gstRate"
                value={formData.gstRate}
                onChange={handleChange}
                className="mt-2 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="0">0% (Exempt)</option>
                <option value="5">5%</option>
                <option value="18">18%</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="tcsRate" className="text-sm font-semibold text-slate-700">TCS Rate (%)</Label>
              <select
                id="tcsRate"
                name="tcsRate"
                value={formData.tcsRate}
                onChange={handleChange}
                className="mt-2 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="0">0% (Not Applicable)</option>
                <option value="2">2% (Flat)</option>
              </select>
            </div>
          </div>

          {/* Cost Summary */}
          <div className="mt-6 p-4 bg-slate-50 rounded-xl">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Cost Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Base Cost:</span>
                <span className="font-medium">₹{totals.baseCost.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">GST ({formData.gstRate}%):</span>
                <span className="font-medium">₹{totals.gstAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">TCS ({formData.tcsRate}%):</span>
                <span className="font-medium">₹{totals.tcsAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-slate-200">
                <span className="font-semibold text-slate-900">Total Cost:</span>
                <span className="font-bold text-lg text-primary-700">₹{totals.totalCost.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Status */}
        <Card className="p-6 border border-slate-200 rounded-xl bg-white">
          <h2 className="text-xl font-heading font-semibold text-slate-900 mb-6">Status</h2>
          
          <div>
            <Label htmlFor="status" className="text-sm font-semibold text-slate-700">Trip Status</Label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="mt-2 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="accepted">Accepted</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="lost">Lost</option>
            </select>
          </div>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/quotes')}
            className="h-12 px-6 rounded-xl"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="h-12 px-8 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg"
            data-testid="create-trip-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Creating...' : 'Create Trip'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateQuote;
